import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CancelIcon from '@mui/icons-material/Cancel';
import StorageIcon from '@mui/icons-material/Storage';
import axios from 'axios';
import { apiString as API_URL } from '../../utils/constants';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { setIsLoading, setShowMessage } from '../../store/shared.slice';
import { recalculateMemberLifecycle } from '../../store/memberLifecycle.slice';
import Authorization from '../../utils/Authorization';
import { RootState } from '../../reducers';
import MessageDisplay from '../Shared/MessageDisplay';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { parentUrl } from '../../utils/constants';
import b64toBlob from 'b64-to-blob';

const DataCorrections = (props) => {
    const history = useNavigate();
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const { isLoading: isRecalculatingLifecycle } = useSelector((state: RootState) => state.memberLifecycle);
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const canExecuteMemberLifecycle = authData.isInPermission('Execute Member Lifecycle');

    const [confirmActiveIndexingModal, setConfirmActiveIndexingModal] = useState(false);
    const [activeProgress, setActiveProgress] = useState<{
        total: number;
        processed: number;
        stage: string;
        isComplete: boolean;
        hasError: boolean;
        isRunning: boolean;
        isCancelled: boolean;
    } | null>(null);

    const fetchActiveProgress = async () => {
        try {
            const res = await axios.get(`${API_URL}/AiIndexing/active-members-progress`);
            if (res.data) {
                const data = {
                    total: res.data.total ?? res.data.Total ?? 0,
                    processed: res.data.processed ?? res.data.Processed ?? 0,
                    stage: res.data.stage ?? res.data.Stage ?? 'Idle',
                    isComplete: res.data.isComplete ?? res.data.IsComplete ?? false,
                    hasError: res.data.hasError ?? res.data.HasError ?? false,
                    isRunning: res.data.isRunning ?? res.data.IsRunning ?? false,
                    isCancelled: res.data.isCancelled ?? res.data.IsCancelled ?? false,
                };
                setActiveProgress((prev) => {
                    if (prev?.isRunning && data.stage === 'Not started' && !data.isComplete) {
                        return prev;
                    }
                    return data;
                });
                return data.isRunning;
            }
        } catch (err) {
            console.error('Error fetching active members progress:', err);
        }
        return false;
    };

    const handleStartActiveIndexing = async () => {
        setConfirmActiveIndexingModal(false);
        setActiveProgress({
            total: 0,
            processed: 0,
            stage: 'Querying active members...',
            isRunning: true,
            isComplete: false,
            hasError: false,
            isCancelled: false,
        });
        try {
            await axios.post(`${API_URL}/AiIndexing/index-active-members`);
            dispatch(setShowMessage(true, 'Active Member AI Indexing task started in background', 'success'));
            setTimeout(fetchActiveProgress, 500);
        } catch (err) {
            setActiveProgress(null);
            dispatch(setShowMessage(true, 'Failed to launch Active Member AI Indexing', 'error'));
        }
    };

    const handleCancelActiveIndexing = async () => {
        try {
            await axios.post(`${API_URL}/AiIndexing/cancel-active-members`);
            dispatch(setShowMessage(true, 'Cancellation request sent', 'info'));
            fetchActiveProgress();
        } catch (err) {
            dispatch(setShowMessage(true, 'Failed to cancel AI Indexing task', 'error'));
        }
    };

    useEffect(() => {
        fetchActiveProgress();
        const interval = setInterval(() => {
            fetchActiveProgress();
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    const fetchRunDataCorrections = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/releasedatafixes`);
            console.log('fetchRunDataCorrections RESPONSE: ', response.data);
            dispatch(setShowMessage(true, response.data, 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const queryUndetermined = async () => {
        dispatch(setIsLoading(true));
        try {
            const data = await axios.get(`${API_URL}/datamanipulation/queryundetermined`);
            let blob = b64toBlob(data.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            let url = window.URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = url;
            const date = new Date();
            const dateInfo = date.toISOString().split('T')[0].replace(/-/g, '');
            link.setAttribute('download', `Inactive with Consent - ${dateInfo}.xlsx`);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting revenue metrics:', error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const fetchBackfillMembersToSearchIndex = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/AiIndexing/backfill-all`);
            console.log('fetchBackfillMembersToSearchIndex RESPONSE: ', response.data);
            dispatch(setShowMessage(true, response.data, 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const processHIE = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/processhie`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const uploadPatienPanel = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/uploadpatientpanel`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const downloadAndProcessClaims = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/downloadclaims`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const processClaims = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/processclaims`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const postMolinaClaimData = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/postmolinaclaimdata`);
            console.log('RESPONSE: ', response);
            const ok = response?.data === true;
            dispatch(setShowMessage(true, ok ? 'Molina claim data posted successfully' : 'Molina claim data post failed', ok ? 'success' : 'error'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const downloadPatientPanelErrors = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/downloadpatientpanelerrors`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const uploadHL7Handler = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/generatehl7`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const addLabImportsToAllAlerts = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/addLabImportsToAllAlerts`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const addHL7ToLabImports = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/addHL7ToLabImports`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const checkActiveEligibilityStatus = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/CheckActiveEligibilityStatus`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const checkActiveUndeterminedEligibilityStatus = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/CheckActiveUndeterminedEligibilityStatus`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const processEligibilityStatus = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/datamanipulation/ProcessEligibilityStatus`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const uploadIcd10 = () => {
        history(`/admin/datacorrections/icd10`);
    };

    // Veradigm Testing below

    const [veradigmUpdates, setVeradigmUpdates] = useState([]);
    const [viewModal, setViewModal] = useState(false);

    const getVeradigmUpdates = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.post(`${API_URL}/integrations/sync-veradigm-medications`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const viewVeradigmAllergyUpdates = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/allergies/GetMembersWithAllergiesFromVeradigm`);
            console.log('RESPONSE: ', response);
            setVeradigmUpdates(response.data);
            setViewModal(true);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const sendNightlySurveys = async () => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.post(`${API_URL}/survey/sendnightlysurveys`);
            console.log('RESPONSE: ', response);
            dispatch(setShowMessage(true, 'Success', 'success'));
            dispatch(setIsLoading(false));
        } catch (error) {
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
            dispatch(setIsLoading(false));
            console.log(error);
        }
    };

    const calculateMemberLifecycleStages = async () => {
        dispatch(recalculateMemberLifecycle());
    };

    const ViewVeradigmUpdatesDialog = () => {
        const columns: GridColDef[] = [
            {
                field: 'Name',
                headerName: 'Name',
                minWidth: 200,
                flex: 1,
                valueGetter: (params: any) => {
                    return `${params.row.FirstName} ${params.row.LastName}`;
                },
            },
        ];
        return (
            <Dialog open={viewModal} onClose={() => setViewModal(false)} maxWidth="md" fullWidth>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>
                        Members who have new allergies downloaded from Veradigm
                    </Typography>
                    <div style={{ height: '50vh', width: '100%' }}>
                        <DataGrid
                            rows={veradigmUpdates}
                            columns={columns}
                            // pageSize={pageSize}
                            pageSizeOptions={[5]}
                            // onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            getRowId={(row) => row.Id}
                            getRowHeight={() => 'auto'}
                            onCellClick={(params: any) => {
                                window.open(`${parentUrl}provider/members-list/member-chart/${params.id}/member-profile`, '_blank');
                            }}
                            sx={{
                                [`& .${gridClasses.cell}`]: {
                                    py: 1,
                                },
                            }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button sx={{ minWidth: '120px' }} variant="outlined" onClick={() => setViewModal(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Veradigm Testing above

    const ConfirmActiveIndexingDialog = () => (
        <Dialog open={confirmActiveIndexingModal} onClose={() => setConfirmActiveIndexingModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Active Member AI Indexing</DialogTitle>
            <DialogContent>
                <Typography variant="body1" paragraph sx={{ mt: 1 }}>
                    Are you sure you want to index <strong>ALL currently active members</strong> into Azure AI Search?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    This task will process all clinical documents, notes, encounters, care plans, and vital signs for active members, embedding them with Azure AI Foundry <code>text-embedding-3-small</code> while respecting token/rate limit quotas.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={() => setConfirmActiveIndexingModal(false)}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleStartActiveIndexing}>
                    Confirm & Start Indexing
                </Button>
            </DialogActions>
        </Dialog>
    );

    useEffect(() => {
        document.title = props.title;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ViewVeradigmUpdatesDialog />
            <ConfirmActiveIndexingDialog />
            <MessageDisplay />
            {isLoading && (
                <Typography marginBottom={1} color={'var(--success)'} variant="body1">
                    Loading...
                </Typography>
            )}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="Data Corrections" />
                        </Divider>
                        <Typography marginBottom={1} color={'#333'} variant="body1">
                            All corrections are idempotent
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            <Button disabled={isLoading} variant="contained" onClick={fetchRunDataCorrections}>
                                Run Data Corrections
                            </Button>
                            <Button disabled={isLoading} variant="contained" color="secondary" onClick={fetchBackfillMembersToSearchIndex}>
                                Backfill Members to Search Index
                            </Button>
                            <Button disabled={isLoading} variant="contained" color="warning" onClick={queryUndetermined}>
                                Query Undetermined With Consent
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="HIE" />
                        </Divider>
                        <Stack direction="column" spacing={2}>
                            <Button disabled={isLoading} variant="contained" onClick={downloadPatientPanelErrors}>
                                Download Patient Panel Errors
                            </Button>
                            <Button disabled={isLoading} variant="contained" color="success" onClick={processHIE}>
                                Process HIE ADT alerts Download
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={uploadHL7Handler}>
                                Upload HL7 to Contexture
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={uploadPatienPanel}>
                                Upload Patient Panel to Contexture
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={addHL7ToLabImports}>
                                Download Lab Imports
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={addLabImportsToAllAlerts}>
                                Change LabImport to AllAlert
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="Claims" />
                        </Divider>
                        <Stack direction="column" spacing={2}>
                            <Button disabled={isLoading} variant="contained" onClick={downloadAndProcessClaims}>
                                Download & Process Claims
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={processClaims}>
                                Process Claims
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={uploadIcd10}>
                                Upload ICD10
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="Member Processing" />
                        </Divider>
                        <Stack direction="column" spacing={2}>
                            <Button disabled={isLoading} variant="contained" onClick={checkActiveEligibilityStatus}>
                                Get Active Eligibility Status from Waystar
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={checkActiveUndeterminedEligibilityStatus}>
                                Get Active Undetermined Eligibility Status from Waystar
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={processEligibilityStatus}>
                                Process Waystar Eligibility Results
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={postMolinaClaimData}>
                                Post Molina Claim Data
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={sendNightlySurveys}>
                                Send Surveys - Check 2 Weeks & Complete
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="AI Management" />
                        </Divider>
                        <Stack direction="column" spacing={2}>
                            <Button
                                disabled={isLoading || (activeProgress?.isRunning ?? false)}
                                variant="contained"
                                color="primary"
                                startIcon={<StorageIcon />}
                                onClick={() => setConfirmActiveIndexingModal(true)}
                            >
                                Index All Active Members
                            </Button>

                            {activeProgress && activeProgress.stage !== 'Not started' && (
                                <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Active Member Indexing Progress
                                        </Typography>
                                        {activeProgress.isRunning && (
                                            <Chip label="RUNNING" color="primary" size="small" />
                                        )}
                                        {activeProgress.isComplete && !activeProgress.isCancelled && (
                                            <Chip label="COMPLETE" color="success" size="small" />
                                        )}
                                        {activeProgress.isCancelled && (
                                            <Chip label="CANCELLED" color="warning" size="small" />
                                        )}
                                    </Stack>

                                    {activeProgress.isRunning && (activeProgress.total === 0 || activeProgress.processed === 0) ? (
                                        <LinearProgress variant="indeterminate" sx={{ height: 10, borderRadius: 5, mb: 1 }} />
                                    ) : (
                                        <LinearProgress
                                            variant="determinate"
                                            value={activeProgress.total > 0 ? Math.min(100, Math.round((activeProgress.processed / activeProgress.total) * 100)) : 0}
                                            sx={{ height: 10, borderRadius: 5, mb: 1 }}
                                        />
                                    )}

                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        {activeProgress.processed} / {activeProgress.total} documents ({activeProgress.total > 0 ? Math.round((activeProgress.processed / activeProgress.total) * 100) : 0}%) — <em>{activeProgress.stage}</em>
                                    </Typography>

                                    {activeProgress.isRunning && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelActiveIndexing}
                                            sx={{ mt: 1 }}
                                        >
                                            Cancel Indexing
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {canExecuteMemberLifecycle && (
                                <Button
                                    disabled={isLoading || isRecalculatingLifecycle}
                                    variant="contained"
                                    startIcon={<AutorenewIcon />}
                                    onClick={calculateMemberLifecycleStages}
                                >
                                    Calculate Member Lifecycle Stages
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} style={{ padding: '8px', margin: '16px', textAlign: 'left' }}>
                        <Divider style={{ marginBottom: '16px' }}>
                            <Chip label="Allergy Classes" />
                        </Divider>
                        <Stack spacing={2} alignItems={'center'}>
                            <Button disabled={isLoading} variant="contained" onClick={getVeradigmUpdates}>
                                Get Veradigm Updates
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={viewVeradigmAllergyUpdates}>
                                View Veradigm Allergy Updates
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
};

export default DataCorrections;
